
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const EmergencyFundCalculator = () => {
  const [monthlyExpenses, setMonthlyExpenses] = useState<string>('3000');
  const [monthsOfExpenses, setMonthsOfExpenses] = useState<string>('6');
  const [currentSavings, setCurrentSavings] = useState<string>('5000');
  const [monthlySavings, setMonthlySavings] = useState<string>('500');
  
  const [targetAmount, setTargetAmount] = useState<number | null>(null);
  const [shortfall, setShortfall] = useState<number | null>(null);
  const [monthsToGoal, setMonthsToGoal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateEmergencyFund = () => {
    try {
      setError(null);
      
      const expenses = parseFloat(monthlyExpenses);
      const months = parseFloat(monthsOfExpenses);
      const current = parseFloat(currentSavings);
      const savings = parseFloat(monthlySavings);

      if (isNaN(expenses) || isNaN(months) || isNaN(current) || isNaN(savings)) {
        setError("Please enter valid numbers");
        return;
      }

      if (expenses <= 0 || months <= 0 || current < 0 || savings < 0) {
        setError("Values must be positive");
        return;
      }

      const target = expenses * months;
      const gap = Math.max(0, target - current);
      const timeToGoal = savings > 0 ? gap / savings : 0;

      setTargetAmount(target);
      setShortfall(gap);
      setMonthsToGoal(timeToGoal);

    } catch (err) {
      setError("Error calculating emergency fund");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">Emergency Fund Calculator</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="monthly-expenses">Monthly Living Expenses ($)</Label>
              <Input
                id="monthly-expenses"
                type="number"
                value={monthlyExpenses}
                onChange={(e) => setMonthlyExpenses(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="months">Months of Expenses to Save</Label>
              <Select value={monthsOfExpenses} onValueChange={setMonthsOfExpenses}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="9">9 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="current-savings">Current Emergency Savings ($)</Label>
              <Input
                id="current-savings"
                type="number"
                value={currentSavings}
                onChange={(e) => setCurrentSavings(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="monthly-savings">Monthly Savings Amount ($)</Label>
              <Input
                id="monthly-savings"
                type="number"
                value={monthlySavings}
                onChange={(e) => setMonthlySavings(e.target.value)}
                className="mt-1"
              />
            </div>

            <Button onClick={calculateEmergencyFund} className="w-full">
              Calculate Emergency Fund
            </Button>

            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {targetAmount !== null && shortfall !== null && monthsToGoal !== null && (
              <div className="space-y-3">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium">Target Emergency Fund</h3>
                  <p className="text-2xl font-bold">${targetAmount.toLocaleString()}</p>
                </div>
                
                {shortfall > 0 ? (
                  <>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h3 className="font-medium">Amount Still Needed</h3>
                      <p className="text-xl font-bold">${shortfall.toLocaleString()}</p>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="font-medium">Time to Reach Goal</h3>
                      <p className="text-xl font-bold">
                        {monthsToGoal.toFixed(1)} months
                        {monthsToGoal > 12 && ` (${(monthsToGoal/12).toFixed(1)} years)`}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-medium">✅ Goal Achieved!</h3>
                    <p className="text-sm">You have reached your emergency fund target.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmergencyFundCalculator;
